import { getAccessToken } from '../authentication/supabaseService';

/** Display order everywhere a type is offered or filtered. */
export const SUPPORT_TYPES = ['feedback', 'support', 'bug', 'feature_request'] as const;
export type SupportType = typeof SUPPORT_TYPES[number];
export type SupportStatus = 'awaiting_support' | 'awaiting_customer' | 'resolved';
export type SupportEmailStatus = 'sent' | 'failed' | 'skipped' | 'not_attempted';

export interface SupportThread {
  id: string;
  accountOwnerId: string;
  accountName: string;
  creatorUserId: string | null;
  creatorName: string;
  restaurantId: string | null;
  restaurantName: string | null;
  type: SupportType;
  status: SupportStatus;
  subject: string;
  version: string;
  lastMessageAt: string;
  waitingSince: string | null;
  createdAt: string;
  updatedAt: string;
  lastMessagePreview: string;
  unread: boolean;
  recipientUserIds?: string[] | null;
  recipients?: { id: string; name: string | null }[];
}

export interface SupportAttachment {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
}

export interface SupportMessage {
  id: string;
  threadId: string;
  sequence: string;
  senderSide: 'customer' | 'support';
  actorUserId: string | null;
  effectiveUserId: string | null;
  actorName: string;
  effectiveName: string;
  isImpersonated: boolean;
  body: string;
  createdAt: string;
  attachments: SupportAttachment[];
  isRead: boolean;
}

export interface SupportThreadFilters {
  cursor?: string;
  limit?: number;
  scope?: 'mine' | 'account';
  status?: SupportStatus;
  type?: SupportType;
  search?: string;
  accountOwnerId?: string;
  unreadOnly?: boolean;
  restaurantId?: string;
  setupOnly?: boolean;
}

export interface SupportRecipient { id: string; name: string; email: string; role: 'admin' | 'staff' }

export interface SupportEmailLog {
  id: number;
  sentAt: string;
  recipientEmail: string;
  status: 'sent' | 'failed';
  error: string | null;
  deliveredAt: string | null;
  openedAt: string | null;
  bouncedAt: string | null;
  metadata?: Record<string, unknown>;
}

export interface SupportSendResult {
  thread: SupportThread;
  message: SupportMessage;
  emailStatus?: SupportEmailStatus;
}

export class SupportApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
    this.name = 'SupportApiError';
  }
}

async function request<T>(path: string, admin = false, init: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  const response = await fetch(`${admin ? '/api/admin/support' : '/api/support'}${path}`, { ...init, headers });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new SupportApiError(response.status, data.code || 'request_failed', data.error || response.statusText);
  }
  return response.status === 204 ? undefined as T : response.json();
}

function queryString(values: object): string {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  });
  const query = params.toString();
  return query ? `?${query}` : '';
}

function messageForm(body: string, files?: File[]): FormData {
  const form = new FormData();
  form.append('body', body);
  files?.forEach(file => form.append('files', file));
  return form;
}

const idPath = (id: string) => `/threads/${encodeURIComponent(id)}`;

export const supportService = {
  listThreads: (filters: SupportThreadFilters = {}, admin = false, signal?: AbortSignal) =>
    request<{ threads: SupportThread[]; nextCursor: string | null }>(`/threads${queryString(filters)}`, admin, { signal }),
  getThread: (id: string, admin = false, signal?: AbortSignal) => request<SupportThread>(idPath(id), admin, { signal }),
  listMessages: (id: string, page: { before?: string; after?: string; limit?: number } = {}, admin = false, signal?: AbortSignal) =>
    request<{ messages: SupportMessage[]; nextCursor: string | null }>(`${idPath(id)}/messages${queryString(page)}`, admin, { signal }),
  createThread: (input: { body: string; type: SupportType; restaurantId?: string; context?: Record<string, string>; clientRequestId: string; files?: File[] }) => {
    const form = messageForm(input.body, input.files);
    form.append('type', input.type);
    form.append('clientRequestId', input.clientRequestId);
    if (input.restaurantId) form.append('restaurantId', input.restaurantId);
    if (input.context) form.append('context', JSON.stringify(input.context));
    return request<SupportSendResult>('/threads', false, { method: 'POST', body: form });
  },
  recipientDirectory: (restaurantId: string, signal?: AbortSignal) =>
    request<{ accountId: string; restaurantName: string; recipients: SupportRecipient[] }>(`/restaurants/${encodeURIComponent(restaurantId)}/recipients`, true, { signal }),
  createAdminThread: (input: { body: string; restaurantId: string; recipientUserIds: string[]; clientRequestId: string; files?: File[] }) => {
    const form = messageForm(input.body, input.files);
    form.append('type', 'support');
    form.append('restaurantId', input.restaurantId);
    form.append('recipientUserIds', JSON.stringify(input.recipientUserIds));
    form.append('clientRequestId', input.clientRequestId);
    form.append('context', JSON.stringify({ pageId: 'setup', path: '/setup' }));
    return request<SupportSendResult>('/threads', true, { method: 'POST', body: form });
  },
  reply: (id: string, input: { body: string; clientMessageId: string; files?: File[] }, admin = false) => {
    const form = messageForm(input.body, input.files);
    form.append('clientMessageId', input.clientMessageId);
    return request<SupportSendResult>(`${idPath(id)}/messages`, admin, { method: 'POST', body: form });
  },
  markRead: (id: string, messageIds: string[], admin = false) =>
    request<void>(`${idPath(id)}/read`, admin, { method: 'PUT', body: JSON.stringify({ messageIds }) }),
  updateThread: (id: string, patch: { version: string; status?: SupportStatus; type?: SupportType }, admin = false) =>
    request<SupportThread>(idPath(id), admin, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteThread: (id: string, version: string) =>
    request<{ attachmentCleanupPending: boolean }>(idPath(id), true, { method: 'DELETE', body: JSON.stringify({ version }) }),
  unread: (admin = false, signal?: AbortSignal) => request<{ count: number }>('/unread', admin, { signal }),
  attachmentUrl: (threadId: string, messageId: string, attachmentId: string, admin = false) =>
    request<{ url: string }>(`${idPath(threadId)}/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`, admin),
  emailLogs: (id: string, signal?: AbortSignal) => request<{ logs: SupportEmailLog[] }>(`${idPath(id)}/email-logs`, true, { signal }),
  resendEmail: (threadId: string, messageId: string) =>
    request<{ emailStatus: SupportEmailStatus }>(`${idPath(threadId)}/messages/${encodeURIComponent(messageId)}/email`, true, { method: 'POST' }),
};
